import type { CSSProperties } from 'react';

interface TypeFilterTabsProps {
  selected: string | null;
  onSelect: (type: string | null) => void;
  counts: { all: number; note: number; task: number; reminder: number };
}

const TABS: Array<{ key: string | null; label: string }> = [
  { key: null, label: 'All' },
  { key: 'note', label: 'Notes' },
  { key: 'task', label: 'Tasks' },
  { key: 'reminder', label: 'Reminders' },
];

export function TypeFilterTabs({ selected, onSelect, counts }: TypeFilterTabsProps) {
  const getCount = (key: string | null) => {
    if (key === null) return counts.all;
    return counts[key as keyof typeof counts] ?? 0;
  };

  return (
    <div style={styles.container}>
      {TABS.map((tab) => {
        const isActive = selected === tab.key;
        const count = getCount(tab.key);
        return (
          <button
            key={tab.key ?? 'all'}
            type="button"
            style={{
              ...styles.tab,
              ...(isActive ? styles.tabActive : {}),
            }}
            onClick={() => onSelect(isActive ? null : tab.key)}
          >
            <span style={{
              ...styles.tabLabel,
              ...(isActive ? styles.tabLabelActive : {}),
            }}>
              {tab.label}
            </span>
            <span style={{
              ...styles.tabCount,
              ...(isActive ? styles.tabCountActive : {}),
            }}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    gap: 'var(--space-2)',
    overflowX: 'auto',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all var(--transition-fast)',
  },
  tabActive: {
    backgroundColor: 'var(--color-accent-light)',
    borderColor: 'var(--color-accent)',
  },
  tabLabel: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
  },
  tabLabelActive: {
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  tabCount: {
    fontSize: '0.625rem',
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 5px',
    borderRadius: 'var(--radius-full)',
    lineHeight: '16px',
    minWidth: '16px',
    textAlign: 'center',
  },
  tabCountActive: {
    color: 'var(--color-accent)',
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
  },
};
